
import React, { useState, useContext, useRef, useEffect } from 'react';
import axios from '../axiosInstance';
import { useNavigate, useLocation } from 'react-router-dom';
import { LanguageContext } from '../LanguageContext';
import useWafCaptcha from '../hooks/useWafCaptcha';
import useCapsLockWarning from '../hooks/useCapsLockWarning';
import CapsLockWarning from '../components/CapsLockWarning';

function ResetPassword() {
  const { language } = useContext(LanguageContext);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [resendStatus, setResendStatus] = useState({ type: null, message: '' });
  const [isResending, setIsResending] = useState(false);
  const navigate = useNavigate();
  const query = new URLSearchParams(useLocation().search);
  const emailQueryParam = query.get('email');
  const flow = query.get('flow');
  const activationFlow = flow === 'activation';
  const successRef = useRef(null);
  const errorRef = useRef(null);
  const { ensureCaptcha, CaptchaOverlay } = useWafCaptcha();
  const capsNew = useCapsLockWarning();
  const capsConfirm = useCapsLockWarning();

  useEffect(() => {
    if (success && successRef.current) {
      successRef.current.focus();
    }
    if (error && errorRef.current) {
      errorRef.current.focus();
    }
  }, [success, error]);

  useEffect(() => {
    if (emailQueryParam) {
      setEmail(emailQueryParam);
    }
  }, [emailQueryParam]);

  const text = {
    en: {
      skip: 'Skip to main content',
      heading: activationFlow ? 'Set your PATH password' : 'Reset your password',
      intro: activationFlow
        ? 'Enter the activation code sent to your email, then choose your PATH password. If you need a new code, you can request one below.'
        : 'Enter the verification code sent to your email, then choose a new password. If you need a new code, you can request one below.',
      email: 'Email address',
      code: 'Verification code',
      newPassword: 'New password',
      confirmPassword: 'Confirm new password',
      submit: activationFlow ? 'Set password' : 'Reset password',
      success: activationFlow
        ? 'Your PATH password has been set. You can sign in when you are ready.'
        : 'Your password has been reset. You can sign in when you are ready.',
      errorEmail: 'Email is required.',
      errorCode: 'Verification code is required.',
      errorMatch: 'Passwords do not match.',
      errorLength: 'Password must be at least 8 characters.',
      errorGeneric: 'Failed to reset password.',
      expiryNotice: 'Verification codes expire after 1 hour.',
  passwordHint: 'Use at least 8 characters with an upper and lower case letter, a number, and a symbol.',
      resendLabel: activationFlow ? 'Resend activation code' : 'Resend code',
      resendHint: activationFlow ? 'Need a new activation code? You can resend it to the email above.' : 'Need a new code? You can resend it to the email above.',
      resendSuccess: activationFlow ? 'If the account exists, a new activation code has been sent.' : 'If the account exists, a new verification code has been sent.',
      resendError: 'Unable to resend the code. Please try again.',
      resendEmailMissing: 'Enter your email address before requesting a new code.'
    },
    fr: {
      skip: 'Passer au contenu principal',
      heading: activationFlow ? 'Définissez votre mot de passe PATH' : 'Réinitialisez votre mot de passe',
      intro: activationFlow
        ? 'Saisissez le code d’activation envoyé à votre courriel, puis choisissez votre mot de passe PATH. Si vous avez besoin d’un nouveau code, vous pouvez en demander un ci-dessous.'
        : 'Saisissez le code de vérification envoyé à votre courriel, puis choisissez un nouveau mot de passe. Si vous avez besoin d’un nouveau code, vous pouvez en demander un ci-dessous.',
      email: 'Adresse courriel',
      code: 'Code de vérification',
      newPassword: 'Nouveau mot de passe',
      confirmPassword: 'Confirmez le nouveau mot de passe',
      submit: activationFlow ? 'Définir le mot de passe' : 'Réinitialiser le mot de passe',
      success: activationFlow
        ? 'Votre mot de passe PATH a été défini. Vous pouvez vous connecter lorsque vous êtes prête.'
        : 'Votre mot de passe a été réinitialisé. Vous pouvez vous connecter lorsque vous êtes prête.',
      errorEmail: 'Adresse électronique requise.',
      errorCode: 'Code de vérification requis.',
      errorMatch: 'Les mots de passe ne correspondent pas.',
      errorLength: 'Le mot de passe doit comporter au moins 8 caractères.',
      errorGeneric: 'Échec de la réinitialisation du mot de passe.',
      expiryNotice: 'Les codes de vérification expirent après 1 heure.',
  passwordHint: 'Utilisez au moins 8 caractères comprenant une majuscule, une minuscule, un chiffre et un symbole.',
      resendLabel: activationFlow ? 'Renvoyer le code d’activation' : 'Renvoyer le code',
      resendHint: activationFlow ? 'Besoin d’un nouveau code d’activation? Vous pouvez le renvoyer au courriel ci-dessus.' : 'Besoin d’un nouveau code? Vous pouvez le renvoyer au courriel ci-dessus.',
      resendSuccess: activationFlow ? 'Si le compte existe, un nouveau code d’activation a été envoyé.' : 'Si le compte existe, un nouveau code de vérification a été envoyé.',
      resendError: 'Impossible de renvoyer le code. Veuillez réessayer.',
      resendEmailMissing: 'Entrez votre adresse courriel avant de demander un nouveau code.'
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setResendStatus({ type: null, message: '' });
    const trimmedEmail = email.trim();
    const trimmedCode = code.trim();
    if (!trimmedEmail) {
      setError(text[language].errorEmail);
      return;
    }
    if (!trimmedCode) {
      setError(text[language].errorCode);
      return;
    }
    if (newPassword !== confirmPassword) {
      setError(text[language].errorMatch);
      return;
    }
    if (newPassword.length < 8) {
      setError(text[language].errorLength);
      return;
    }
    try {
      await ensureCaptcha();
      await axios.post('/api/auth/confirm-forgot-password', {
        email: trimmedEmail,
        code: trimmedCode,
        newPassword
      });
      setSuccess(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      setError(err.response?.data?.message || text[language].errorGeneric);
    }
  };

  const handleResend = async () => {
    const trimmedEmail = email.trim();
    setResendStatus({ type: null, message: '' });
    if (!trimmedEmail) {
      setResendStatus({ type: 'error', message: text[language].resendEmailMissing });
      return;
    }
    try {
      setIsResending(true);
      await ensureCaptcha();
      await axios.post('/api/auth/forgot-password', {
        email: trimmedEmail,
        requestRoute: '/reset-password',
        requestFlow: activationFlow ? 'activation' : 'forgot-password'
      });
      setResendStatus({ type: 'success', message: text[language].resendSuccess });
    } catch (err) {
      setResendStatus({ type: 'error', message: err.response?.data?.message || text[language].resendError });
    } finally {
      setIsResending(false);
    }
  };

  if (success) {
    return (
      <>
        <CaptchaOverlay />
        <a href="#main-content" className="govuk-skip-link">{text[language].skip}</a>
        <div
          role="status"
          tabIndex={-1}
          className="govuk-panel govuk-panel--confirmation success-message"
          ref={successRef}
          id="main-content"
        >
          {text[language].success}
        </div>
      </>
    );
  }

  return (
    <>
      <CaptchaOverlay />
      <a href="#main-content" className="govuk-skip-link">{text[language].skip}</a>
      <main className="govuk-main-wrapper govuk-width-container" role="main" id="main-content" tabIndex="-1">
        <h1 className="govuk-heading-l">{text[language].heading}</h1>
        <p className="govuk-hint">{text[language].intro}</p>
        <form
          onSubmit={handleSubmit}
          aria-label={language === 'fr'
            ? (activationFlow ? 'Formulaire de définition du mot de passe PATH' : 'Formulaire de réinitialisation du mot de passe')
            : (activationFlow ? 'PATH password setup form' : 'Reset password form')}
          className="govuk-form-group reset-password-form"
          autoComplete="on"
        >
          <div className="govuk-form-group">
            <label className="govuk-label" htmlFor="reset-email">{text[language].email}</label>
            <input
              className="govuk-input"
              id="reset-email"
              name="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={e => setEmail(e.target.value)}
              aria-label={text[language].email}
            />
          </div>
          <div className="govuk-form-group">
            <label className="govuk-label" htmlFor="verification-code">{text[language].code}</label>
            <span className="govuk-hint">{text[language].expiryNotice}</span>
            <input
              className="govuk-input"
              id="verification-code"
              name="code"
              type="text"
              autoComplete="one-time-code"
              required
              value={code}
              onChange={e => setCode(e.target.value)}
              aria-label={text[language].code}
            />
          </div>
          <div className="govuk-form-group">
            <label className="govuk-label" htmlFor="new-password">{text[language].newPassword}</label>
            <span className="govuk-hint">{text[language].passwordHint}</span>
            <input
              className="govuk-input"
              id="new-password"
              name="new-password"
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              aria-label={text[language].newPassword}
              {...capsNew.bind}
            />
            <CapsLockWarning show={capsNew.show} language={language} />
          </div>
          <div className="govuk-form-group">
            <label className="govuk-label" htmlFor="confirm-password">{text[language].confirmPassword}</label>
            <input
              className="govuk-input"
              id="confirm-password"
              name="confirm-password"
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              aria-label={text[language].confirmPassword}
              {...capsConfirm.bind}
            />
            <CapsLockWarning show={capsConfirm.show} language={language} />
          </div>
          {error && (
            <div
              className="govuk-error-message error-message"
              role="alert"
              aria-live="assertive"
              tabIndex="-1"
              ref={errorRef}
            >
              {error}
            </div>
          )}
          {resendStatus.message && (
            <div
              className={resendStatus.type === 'error' ? 'govuk-error-message' : 'govuk-hint'}
              role={resendStatus.type === 'error' ? 'alert' : 'status'}
              aria-live={resendStatus.type === 'error' ? 'assertive' : 'polite'}
            >
              {resendStatus.message}
            </div>
          )}
          <div className="govuk-hint">{text[language].resendHint}</div>
          <div className="govuk-button-group">
            <button type="submit" className="govuk-button">{text[language].submit}</button>
            <button
              type="button"
              className="govuk-button govuk-button--secondary"
              onClick={handleResend}
              disabled={isResending}
            >
              {isResending ? `${text[language].resendLabel}...` : text[language].resendLabel}
            </button>
          </div>
        </form>
      </main>
    </>
  );
}

export default ResetPassword;
